import _ from 'lodash';
import { VantageNames } from '@/vantage/constants';
import type { AnyProperty } from '@/utilities.types';
import type { ContextContentValue, ContextSchema } from '@/vantage/types/Context.types';
import type { RowNode } from 'ag-grid-community';

export function applyReduction(
  objects: ContextSchema[],
  reducer: string | undefined,
  contextContent = (ctx: ContextSchema) => ctx.content?.value,
  category: string | undefined = undefined,
): ContextContentValue | ContextContentValue[] | null | undefined {
  if (!objects || objects.length === 0) {
    return null;
  }

  const filteredObjects = category ? objects.filter((obj) => obj.category === category) : objects;

  if (filteredObjects.length === 0) return null;

  if (reducer === 'mostRecent' || reducer === 'leastRecent') {
    const reduced = filteredObjects.reduce((targetObject, currentObject) => {
      const targetTime = new Date(targetObject.timeUpdated);
      const currentTime = new Date(currentObject.timeUpdated);
      if (reducer === 'leastRecent') {
        return currentTime < targetTime ? currentObject : targetObject;
      } else {
        return currentTime > targetTime ? currentObject : targetObject;
      }
    });
    return contextContent(reduced);
  } else if (reducer === 'list') {
    return filteredObjects.map(contextContent);
  } else if (reducer === 'set') {
    return _.uniq(_.compact(filteredObjects.map(contextContent)));
  } else if (reducer === 'count') {
    return filteredObjects.length;
  }
}

export function booleanOrValue(val: string) {
  if (val === 'true') {
    return true;
  } else if (val === 'false') {
    return false;
  } else {
    return val;
  }
}

export function diffRows<T>(
  oldRows: T[],
  newRows: T[],
  getId: (item: T) => string | number,
): {
  add: T[];
  update: T[];
  remove: T[];
} {
  const oldRowsMap = new Map(oldRows.map((item) => [getId(item), item]));
  const newRowsMap = new Map(newRows.map((item) => [getId(item), item]));

  return {
    add: newRows.filter((newItem) => !oldRowsMap.has(getId(newItem))),
    update: newRows.filter((newItem) => {
      const oldItem = oldRowsMap.get(getId(newItem));
      return oldItem !== undefined && !_.isEqual(newItem, oldItem);
    }),
    remove: oldRows.filter((oldItem) => !newRowsMap.has(getId(oldItem))),
  };
}

export const ensureColumnVisible = (columnDefs: { headerName?: string; hide: boolean }[]) => {
  if (columnDefs.length > 0 && columnDefs.every((d: { hide: boolean }) => d.hide)) {
    let foundConditionColumn = false;
    const newColumns = columnDefs.map((d) => {
      if (d.headerName === `${VantageNames.Columns.Condition}*`) {
        foundConditionColumn = true;
        d.hide = false;
      }
      return d;
    });
    if (!foundConditionColumn) {
      newColumns[0].hide = false;
    }
    return newColumns;
  } else {
    return columnDefs;
  }
};

export function constructKey(part1: string, part2: string) {
  return `${part1}-${part2}`;
}

export function nameToReferenceKey(name: string) {
  return `${name}*`;
}

export function referenceKeyToName(key: string) {
  return key?.replace(/\*$/, '');
}

export interface Node {
  node?: { group: any; allLeafChildren: Node[] | null; hasVisibleChildren?: boolean };
  data?: AnyProperty;
}
/**
 * Recursively collects **leaf** `RowNode`s that survive the current filter.
 * @param root  The starting node (usually `params.rowNode` inside an aggFunc)
 * @returns     Array of leaf RowNodes, honouring `childrenAfterAggFilter` and `childrenAfterFilter`
 */
export function leavesAfterFilter(node: RowNode): RowNode[] {
  if (!node.group) return [node];
  const kids = node.childrenAfterAggFilter ?? node.childrenAfterFilter ?? [];
  return kids.flatMap(leavesAfterFilter);
}

export const nodeDurationMillis = (node: Node): number | undefined => {
  if (node?.node?.group) {
    return node.node.allLeafChildren!.reduce((sum: number, leafNode) => sum + (nodeDurationMillis?.(leafNode) ?? 0), 0);
  }

  const start = node?.data?.[VantageNames.Columns.Start];
  if (!start) {
    return undefined;
  }
  const startDate = Date.parse(start);

  const end = node?.data?.[VantageNames.Columns.End];
  const endDate = end ? Date.parse(end) : Date.now();

  const duration = endDate - startDate;
  if (duration < 0) {
    return undefined;
  }

  return duration;
};

export const capsuleContextId = (ctx: ContextSchema) => `${ctx.conditionId}:${ctx.kindId}`;

export const valueOrDefault = (
  defaultValue: any,
  transformation: (contextArrayOfInterest: any) => ContextSchema[],
  contexts: any,
) => {
  if (contexts.length) {
    return transformation(contexts);
  } else if (
    [null, undefined].includes(defaultValue) ||
    (!['boolean', 'number'].includes(typeof defaultValue) &&
      (Object.keys(defaultValue).length === 0 || defaultValue.length === 0))
  ) {
    return null;
  } else {
    return defaultValue;
  }
};

export const contextContent = (contextPath: string | (string | number)[]) => {
  return contextPath
    ? (ctx: ContextSchema) => _.get(ctx, contextPath, null)
    : (ctx: ContextSchema) => ctx.content?.value;
};

export const getKindIdentifierMatchFromContextMap = (
  contextMap: Map<string, ContextSchema[]>,
  kind: string,
  identifier: string,
): ContextSchema[] => contextMap.get(constructKey(kind, identifier)) ?? [];
